import React, { useState } from 'react'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import DarkModeIcon from '@mui/icons-material/DarkMode'
import EmailIcon from '@mui/icons-material/Email'
import LightModeIcon from '@mui/icons-material/LightMode'
import LockIcon from '@mui/icons-material/Lock'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import { Link, useLocation, useNavigate } from 'react-router'
import { PrimaryButton } from '../components/Buttons'
import cnergiLogo from '../resources/CNERGI_PNG@4x.png'
import {
  getLoginUserRole,
  getRememberedLoginEmail,
  loginUser,
  saveLoginSession,
  saveRememberedLoginEmail,
} from '../services/authApi'
import './MobileLoginPage.css'

function MobileLoginPage({ isDarkMode = false, onToggleTheme }) {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false)
  const location = useLocation()
  const rememberedEmail = getRememberedLoginEmail()
  const [formValues, setFormValues] = useState({
    email: location.state?.email || rememberedEmail,
    password: '',
    remember: true,
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [message, setMessage] = useState('')
  const navigate = useNavigate()

  const handleChange = (event) => {
    const { checked, name, type, value } = event.target

    setFormValues((currentValues) => ({
      ...currentValues,
      [name]: type === 'checkbox' ? checked : value,
    }))
  }

  const handleSubmit = async (event) => {
    event.preventDefault()

    if (isSubmitting) {
      return
    }

    setIsSubmitting(true)
    setMessage('')

    try {
      const loginResponse = await loginUser({
        email: formValues.email.trim(),
        password: formValues.password,
      })

      saveRememberedLoginEmail(formValues.email, formValues.remember)
      saveLoginSession(loginResponse, formValues.remember)
      const destination = getLoginUserRole(loginResponse) === 'tech' ? '/tech' : '/mobile/maindash'
      navigate(destination, { replace: true })
    } catch (error) {
      setMessage(error.message || 'Unable to sign in. Please check your email and password.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <main className="mobile-login">
      <header className="mobile-login__topbar">
        <Link className="mobile-login__icon-link" to="/mobile" aria-label="Back to mobile home">
          <ArrowBackIcon aria-hidden="true" />
        </Link>

        <Link className="mobile-login__brand" to="/" aria-label="CNERGI home">
          <img src={cnergiLogo} alt="CNERGI" />
        </Link>

        <button
          className="mobile-login__icon-button"
          type="button"
          aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          onClick={onToggleTheme}
        >
          {isDarkMode ? <LightModeIcon aria-hidden="true" /> : <DarkModeIcon aria-hidden="true" />}
        </button>
      </header>

      <section className="mobile-login__intro" aria-labelledby="mobile-login-title">
        <p>CNERGI Mobile</p>
        <h1 id="mobile-login-title">Sign in to your EMS portal.</h1>
      </section>

      <form className="mobile-login__form" onSubmit={handleSubmit}>
        <label className="mobile-login__field">
          <span>Email address</span>
          <div className="mobile-login__input-wrap">
            <EmailIcon aria-hidden="true" />
            <input
              type="email"
              name="email"
              placeholder="you@company.com"
              autoComplete="email"
              value={formValues.email}
              onChange={handleChange}
              required
            />
          </div>
        </label>

        <label className="mobile-login__field">
          <span>Password</span>
          <div className="mobile-login__input-wrap">
            <LockIcon aria-hidden="true" />
            <input
              type={isPasswordVisible ? 'text' : 'password'}
              name="password"
              placeholder="Enter your password"
              autoComplete="current-password"
              value={formValues.password}
              onChange={handleChange}
              required
            />
            <button
              className="mobile-login__password-toggle"
              type="button"
              aria-label={isPasswordVisible ? 'Hide password' : 'Show password'}
              onClick={() => setIsPasswordVisible((current) => !current)}
            >
              {isPasswordVisible ? <VisibilityOffIcon aria-hidden="true" /> : <VisibilityIcon aria-hidden="true" />}
            </button>
          </div>
        </label>

        <div className="mobile-login__options">
          <label>
            <input
              type="checkbox"
              name="remember"
              checked={formValues.remember}
              onChange={handleChange}
            />
            <span>Remember me</span>
          </label>
          <a href="#forgot-password">Forgot password?</a>
        </div>

        <PrimaryButton className="mobile-login__submit" type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Signing in...' : 'Sign In'}
        </PrimaryButton>

        {message ? (
          <p className="mobile-login__message" role="alert" aria-live="polite">
            {message}
          </p>
        ) : null}

        <p className="mobile-login__register">
          Need an account? <Link to="/register">Create one</Link>
        </p>
      </form>
    </main>
  )
}

export default MobileLoginPage
